import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { createLabel } from 'common/components/InsuranceSelect/InsuranceSelect';
import {
  Div,
  FlexCol,
  InputLabel,
  StyledFormItem,
} from 'common/styles/oc-styled-components';
import { Autocomplete } from 'pages/common/components/Autocomplete/Autocomplete';
import { DIRECTION } from 'pages/common/components/Autocomplete/types';

import { usePrevious } from 'utils/customHooks/usePrevious';

import {
  selectDataInsuranceList,
  selectDataMatchId,
} from '../../../../connect/selectors';
import {
  DIMENSIONS,
  useWindowDimensions,
} from '../../../../providers/WindowDimensionsProvider';
import { ResponsiveInputRow } from '../../../legacy-checkout/checkout-styled-components';

import { SelectSkeleton } from './styled-components';

const InsurancePickerUS = ({
  defaultPlan,
  defaultProvider,
  form,
  updateInsurance,
  checkRequiredFields,
  planIsOptional,
}) => {
  const [selectedProviderId, setSelectedProviderId] = useState(
    (defaultProvider || {}).id,
  );
  const [selectedPlan, setSelectedPlan] = useState(defaultPlan);
  const previousSelectedPlan = usePrevious(selectedPlan);

  const { layout } = useWindowDimensions();

  const insuranceList = useSelector(selectDataInsuranceList);
  const directLink = !useSelector(selectDataMatchId);

  // There is a flow that allows users to skip the insurance step in the funnel, which causes
  // our data to not have provider.id. In those cases, we need to use the provider.name to filter
  useEffect(() => {
    if (
      insuranceList
      && !selectedProviderId
      && defaultProvider
      && !directLink
    ) {
      setSelectedProviderId(
        insuranceList.find((provider) => provider.name === defaultProvider.name)
          .id,
      );
    }
  }, [insuranceList]);

  const plans = useMemo(() => {
    if (!insuranceList?.length || !selectedProviderId) {
      return [];
    }
    const plansByProvider = insuranceList.find(
      (provider) => provider.id === selectedProviderId,
    );
    if (!plansByProvider) {
      return [];
    }

    return plansByProvider.plans;
  }, [selectedProviderId, insuranceList]);

  useEffect(() => {
    if (!insuranceList || !plans) {
      return;
    }

    // Trigger if plan was cleared or it's optional.
    if (
      selectedProviderId
      && ((previousSelectedPlan && !selectedPlan) || planIsOptional)
    ) {
      const provider = {
        id: selectedProviderId,
        name: insuranceList.find((p) => p.id === selectedProviderId).name,
      };

      updateInsurance(provider, null);
    }

    // Trigger when provider and plan selected
    if (selectedPlan && previousSelectedPlan !== selectedPlan) {
      const provider = {
        id: selectedProviderId,
        name: insuranceList.find((p) => p.id === selectedProviderId).name,
      };
      const plan = plans.find((p) => p.id === selectedPlan);

      if (plan) {
        updateInsurance(provider, {
          id: plan.id,
          name: plan.name,
          isSocialAssistance: plan.isSocialAssistance,
        });
      }
    }

    checkRequiredFields();
  }, [
    insuranceList,
    plans,
    selectedProviderId,
    selectedPlan,
    previousSelectedPlan,
  ]);

  const handleSelect = (e) => {
    setSelectedProviderId(e);

    if (form) {
      form.setFieldsValue({
        insuranceCarrierUS: e,
        insurancePlanUS: undefined,
      });
    }
    setSelectedPlan(undefined);
    checkRequiredFields();
  };

  const handlePlanSelect = (e) => {
    if (form) {
      form.setFieldsValue({
        insurancePlanUS: e,
      });
    }
    setSelectedPlan(e);
    checkRequiredFields();
  };

  const filterAndSortInsurance = (list) => [
    list.filter((p) => p.isPopular).sort((a, b) => a.name.localeCompare(b.name)),
    list.filter((p) => !p.isPopular).sort((a, b) => a.name.localeCompare(b.name)),
  ];

  const createCombinedProvidersList = () => {
    const [popularProviders, otherProviders] = filterAndSortInsurance(
      insuranceList,
    );

    return [
      ...popularProviders.length ? [createLabel('Popular Providers')] : [],
      ...popularProviders,
      ...otherProviders.length ? [createLabel('Other Providers')] : [],
      ...otherProviders,
    ];
  };

  const createCombinedPlansList = () => {
    const [popularPlans, otherPlans] = filterAndSortInsurance(plans);

    return [
      ...popularPlans.length ? [createLabel('Popular Plans')] : [],
      ...popularPlans,
      ...otherPlans.length ? [createLabel('Other Plans')] : [],
      ...otherPlans,
    ];
  };

  return (
    <ResponsiveInputRow data-testid="insurancePickerUS">
      <FlexCol margin="0 15px 0 0" padding="6px 0 0" width="100%">
        {!insuranceList
        && (
          <StyledFormItem margin={0}>
            <div data-testid="ProviderSkeleton">
              <SelectSkeleton active margin="0 10px 0 0" paragraph={false} />
            </div>
          </StyledFormItem>
        )}
        {insuranceList
        && (
          <StyledFormItem
            margin={0}
            name="insuranceCarrierUS"
            rules={[
              {
                required: true,
                message:
                  'Required. Check your insurance membership card or employer website',
              },
            ]}
            initialValue={selectedProviderId}
            validateTrigger="onSubmit"
          >
            <Div position="relative">
              <Autocomplete
                testid="Provider"
                preferredDropDirection={layout === DIMENSIONS.MOBILE
                  ? DIRECTION.DOWN
                  : DIRECTION.UP}
                onSelect={handleSelect}
                options={createCombinedProvidersList()}
                defaultSelected={selectedProviderId}
              />
              <InputLabel>Insurance Carrier</InputLabel>
            </Div>
          </StyledFormItem>
        )}
      </FlexCol>
      <FlexCol padding="6px 0 0" width="100%">
        {!insuranceList
        && (
          <StyledFormItem margin={0}>
            <SelectSkeleton
              active
              data-testid="PlanSkeleton"
              margin="0 10px 0 0"
              paragraph={false}
            />
          </StyledFormItem>
        )}
        {insuranceList
        && (
          <StyledFormItem
            margin={0}
            name="insurancePlanUS"
            rules={[
              {
                required: true,
                message:
                  'Required. Check your insurance membership card or employer website',
              },
            ]}
            initialValue={selectedPlan}
            validateTrigger="onSubmit"
          >
            <Div position="relative">
              <Autocomplete
                testid="plan"
                preferredDropDirection={layout === DIMENSIONS.MOBILE
                  ? DIRECTION.DOWN
                  : DIRECTION.UP}
                onSelect={handlePlanSelect}
                options={createCombinedPlansList()}
                defaultSelected={selectedPlan}
              />

              <InputLabel>
                Insurance Plan{planIsOptional ? ' (Optional)' : ''}
              </InputLabel>
            </Div>
          </StyledFormItem>
        )}
      </FlexCol>
    </ResponsiveInputRow>
  );
};

InsurancePickerUS.defaultProps = {
  defaultPlan: null,
  defaultProvider: null,
  checkRequiredFields: () => {},
  planIsOptional: false,
};

InsurancePickerUS.propTypes = {
  defaultPlan: PropTypes.number,
  defaultProvider: PropTypes.object,
  form: PropTypes.object,
  updateInsurance: PropTypes.func.isRequired,
  checkRequiredFields: PropTypes.func,
  planIsOptional: PropTypes.bool,
};

export default InsurancePickerUS;
